import { Box, Button, Card, CircularProgress, Container, Typography, MenuItem } from "@mui/material";
import { useState } from "react";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import Iconify from "src/components/iconify/iconify";
import { getQuarterStartDate, getQuarterEndDate, formatQuarterForPreview, generateYearRange, getCurrentQuarter, getCurrentYear, today, addDaysToDate, formatDateForPreview } from "src/utils/date-utils";
import TruxSimpleSelectField from "src/components/inputs/trux-simple-select-field";
import { ColoredIcon } from "src/components/colored-icon";
import TruxSimpleDateField from "src/components/inputs/trux-simple-date-field";

export default function Page() {
  const settings = useSettingsContext();

  const currentYear = getCurrentYear();
  const currentQuarter = getCurrentQuarter();
  const yearRange = generateYearRange(5, 5);

  const [year, setYear] = useState(currentYear);
  const [quarter, setQuarter] = useState(currentQuarter);

  const [loading, setLoading] = useState(false);
  const [csvUrl, setCsvUrl] = useState(null as string | null);

  const [byDate, setByDate] = useState(false);
  const [fromDateParam, setFromDateParam] = useState(addDaysToDate(today(), -30));
  const [toDateParam, setToDateParam] = useState(today());


  const fromDate = byDate ? fromDateParam : getQuarterStartDate(year, quarter);
  const toDate = byDate ? toDateParam : getQuarterEndDate(year, quarter);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <Card sx={{p: 4, display: "flex", flexDirection:"column", alignItems: "center", gap: 4}}>
        {!csvUrl && <Typography variant="h4">
          IFTA Report
        </Typography>}

        <Box maxWidth="300px" width="100%" display="flex" flexDirection="column" alignItems="center" gap={2}>
          {csvUrl ? <>
              <Box textAlign="center">
                <ColoredIcon color="success">
                  <Iconify icon="solar:check-circle-bold" width={44} />
                </ColoredIcon>
              </Box>
              <Box textAlign="center">
                IFTA File successfully generated <br /> {byDate ? <>{formatDateForPreview(fromDate)} to {formatDateForPreview(toDate)}</> : formatQuarterForPreview(year, quarter)}
              </Box>

              <Box display="flex" flexDirection="row" gap={2}>
                <Button
                  variant="outlined"
                  color="inherit"
                  sx={{mt:2}} onClick={() => {
                    setLoading(false);
                    setCsvUrl(null);
                  }}>
                  Back
                </Button>
                <Button
                  variant="outlined"
                  color="success"
                  sx={{mt:2}}
                  startIcon={<Iconify icon="basil:file-download-outline" />}
                  href={csvUrl}
                  download={`IFTA Report ${byDate ? `${fromDate} ${toDate}` : formatQuarterForPreview(year, quarter)}.csv`}
                  target="_blank"
                  rel="noopener noreferrer">
                  Download
                </Button>
              </Box>
            </> : <>
            {loading && <Box display="flex" flexDirection="column" alignItems="center" gap={4} pt={6} pb={6}>
            <CircularProgress size={50} sx={{mt: 5}} />
            <Box><i>Downloading...</i></Box>
          </Box>}

          {!loading && <>
            <Box width="100%">
              <Typography component="div" fontWeight={700}>By</Typography>
              <TruxSimpleSelectField value={byDate} onChange={val => setByDate(val === "true")}>
                <MenuItem value="false">Quarter</MenuItem>
                <MenuItem value="true">Date</MenuItem>
              </TruxSimpleSelectField>
            </Box>
            {byDate ? <>
                <Box width="100%">
                  <Typography component="div" fontWeight={700}>From Date</Typography>
                  <TruxSimpleDateField value={fromDateParam} onChange={d => setFromDateParam(d!)} limitWidth={false} />
                </Box>
                <Box width="100%">
                  <Typography component="div" fontWeight={700}>To Date</Typography>
                  <TruxSimpleDateField value={toDateParam} onChange={d => setToDateParam(d!)} limitWidth={false} />
                </Box>
              </> :
              <>
                <Box width="100%">
                  <Typography component="div" fontWeight={700}>Year</Typography>
                  <TruxSimpleSelectField value={year} onChange={setYear}>
                    {yearRange.map(yearOption => (
                      <MenuItem key={yearOption} value={yearOption}>{yearOption}</MenuItem>
                    ))}
                  </TruxSimpleSelectField>
                </Box>

                <Box width="100%">
                  <Typography component="div" fontWeight={700}>Quarter</Typography>
                  <TruxSimpleSelectField value={quarter} onChange={setQuarter}>
                    <MenuItem value={1}>Q1 (Jan - Mar)</MenuItem>
                    <MenuItem value={2}>Q2 (Apr - Jun)</MenuItem>
                    <MenuItem value={3}>Q3 (Jul - Sep)</MenuItem>
                    <MenuItem value={4}>Q4 (Oct - Dec)</MenuItem>
                  </TruxSimpleSelectField>
                </Box>
              </>
            }

            <Box display="flex" gap={2}>
              <Button variant="contained" color="success" sx={{mt:2}} startIcon={<Iconify icon="basil:file-download-outline" />} onClick={() => {
                setLoading(true);

                axios({url: formatEndpoint(endpoints.eldIftaReportCsv, {fromDate, toDate}), method: 'GET',
                  responseType: 'blob',}).then((response) => {
                  setLoading(false);
                  const blob = new Blob([response.data], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);

                  setCsvUrl(url);
                }).catch(() => {
                  setLoading(false);
                  enqueueSnackbar("Unexpected exception has occured. Please contact customer support.", {variant: "error"});
                });
              }}>
                Generate CSV
              </Button>
              <Button variant="contained" color="success" sx={{mt:2}} startIcon={<Iconify icon="basil:file-download-outline" />} onClick={() => {
                setLoading(true);

                axios({url: formatEndpoint(endpoints.eldIftaReportPdf, {fromDate, toDate}), method: 'GET',
                  responseType: 'blob',}).then((response) => {
                  setLoading(false);
                  const blob = new Blob([response.data], { type: 'application/pdf' });
                  const url = window.URL.createObjectURL(blob);

                  const pdfWindow = window.open();
                  if (pdfWindow) {
                    pdfWindow.location.href = url;
                  }
                }).catch((e1) => {
                  console.error(e1);
                  enqueueSnackbar("Unexpected exception has occurred. Please contact customer support.", {variant: "error"});
                });
              }}>
                Generate PDF
              </Button>
            </Box>
          </>}
          </>}
        </Box>
      </Card>
    </Container >
  );
}
